import { ActionIcon, Flex, Tooltip } from "@mantine/core";
import type { TablerIcon } from "@tabler/icons-react";
import * as _ from "lodash-es";
import type {
  MRT_Cell,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance,
  MRT_TableOptions
} from "mantine-react-table-open";
import { OrangeCircle } from "../components/OrangeCircle/OrangeCircle";
import { DeleteButton } from "./mrt/components/rowActions/DeleteButton";
import { EditButton } from "./mrt/components/rowActions/EditButton";
import { submitOrCancelOnKeyPress } from "./mrt/utils/submitOrCancelOnKeyPress";

interface CustomAction<TData extends MRT_RowData> {
  text: string;
  icon?: TablerIcon;
  dynamicIcon?: (row) => TablerIcon;
  loading?: (row: TData) => boolean;
  color?: string;
  enableAction?: (cell: MRT_Cell<TData>) => boolean;
  onClick: (row: MRT_Row<TData>) => void;
}

interface DefaultMRTRowEditModeOptionsOptions<TData extends MRT_RowData> {
  rowActions?: {
    edit?: {
      text: string;
      enableEdit?: (cell: MRT_Cell<TData>) => boolean;
      onClick?: (row: MRT_Row<TData>) => void;
    };
    delete?: {
      text: string;
      enableDelete?: (cell: MRT_Cell<TData>) => boolean;
      onClick: (row: MRT_Row<TData>) => void;
    };
    other?: Array<CustomAction<TData>>;
    highlightRowEdit?: (row: MRT_Row<TData>) => boolean | undefined;
  };
  displayColumnDefOptions?: MRT_TableOptions<TData>["displayColumnDefOptions"];
}

/** Define re-useable default Mantine-React-Table options for row edit mode */
export function getDefaultMRTRowEditModeOptions<TData extends MRT_RowData>(
  options: DefaultMRTRowEditModeOptionsOptions<TData> = {}
): Partial<MRT_TableOptions<TData>> {
  function isCreatingOrEditing(table: MRT_TableInstance<TData>): boolean {
    return (
      table.getState().creatingRow !== null ||
      table.getState().editingRow !== null
    );
  }

  function isActionDisabled(
    table: MRT_TableInstance<TData>,
    cell: MRT_Cell<TData>,
    customAction: CustomAction<TData>
  ): boolean {
    if (isCreatingOrEditing(table)) {
      return true;
    }

    if (customAction.enableAction) {
      return !customAction.enableAction(cell);
    }

    return false;
  }

  function isEditDisabled(
    table: MRT_TableInstance<TData>,
    cell: MRT_Cell<TData>
  ): boolean {
    if (isCreatingOrEditing(table)) {
      return true;
    }

    if (options.rowActions?.edit?.enableEdit) {
      return !options.rowActions.edit.enableEdit(cell);
    }

    return false;
  }

  function isDeleteDisabled(
    table: MRT_TableInstance<TData>,
    cell: MRT_Cell<TData>
  ): boolean {
    if (isCreatingOrEditing(table)) {
      return true;
    }

    if (options.rowActions?.delete?.enableDelete) {
      return !options.rowActions.delete.enableDelete(cell);
    }

    return false;
  }

  return {
    createDisplayMode: "row",
    displayColumnDefOptions: _.merge(
      {
        "mrt-row-actions": {
          header: ""
        }
      },
      options.displayColumnDefOptions
    ),
    editDisplayMode: "row",
    enableEditing: true,
    mantineEditTextInputProps: ({ table }) => ({
      onKeyDown: (event) => submitOrCancelOnKeyPress(event, table)
    }),
    positionToolbarAlertBanner: "none",

    renderRowActions: (renderRowActionsProps) => {
      const { cell, row, table } = renderRowActionsProps;

      return (
        <Flex align="center" gap="sm" justify="flex-end">
          {options.rowActions?.highlightRowEdit?.(row) && <OrangeCircle />}
          {options.rowActions?.edit && (
            <EditButton
              disabled={isEditDisabled(table, cell)}
              label={options.rowActions?.edit?.text}
              onClick={() => {
                table.setEditingRow(row);
                options.rowActions?.edit?.onClick?.(row);
              }}
            />
          )}
          {options.rowActions?.other?.map((customAction, index) => {
            const Icon = customAction.dynamicIcon
              ? customAction.dynamicIcon(row.original)
              : customAction.icon
                ? customAction.icon
                : null;
            return (
              <Tooltip key={index} label={customAction.text}>
                <ActionIcon
                  aria-label={customAction.text}
                  color={customAction.color || "black"}
                  disabled={isActionDisabled(table, cell, customAction)}
                  loaderProps={{ type: "oval" }}
                  loading={customAction.loading?.(row.original)}
                  size="xs"
                  variant="transparent"
                  onClick={() => customAction.onClick(row)}
                >
                  {Icon !== null && <Icon className="other-icon" />}
                </ActionIcon>
              </Tooltip>
            );
          })}
          {options.rowActions?.delete && (
            <DeleteButton
              disabled={isDeleteDisabled(table, cell)}
              label={options.rowActions?.delete?.text}
              onClick={() => options.rowActions?.delete?.onClick(row)}
            />
          )}
        </Flex>
      );
    }
  };
}
